import { Box, Stack, Typography } from "@mui/material";
import React from "react";
import { ReactComponent as CloudError } from "../../assets/svg/CloudError.svg";
import SvgToIcon from "../../icons/SvgToIcon";
import { navigateHome } from "../../utils/utils";
import ContainedButton from "../buttons/ContainedButton";
import OutlinedButton from "../buttons/OutlinedButton";
import ContactSupportModal from "../dialogs/ContactSupportModal";
/**
 * Catch-all error handler that prevents blank pages by capturing React-tree crashes,
 * global JS errors, and unhandled promise rejections.
 */
class GlobalErrorBoundaryInner extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, showModal: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("Uncaught runtime error:", error, errorInfo);
  }

  componentDidMount() {
    window.addEventListener("error", this.handleEventError);
    window.addEventListener("unhandledrejection", this.handlePromiseError);
  }

  componentWillUnmount() {
    window.removeEventListener("error", this.handleEventError);
    window.removeEventListener("unhandledrejection", this.handlePromiseError);
  }

  handleEventError = (event) => this.setError(event.error || event.message);

  handlePromiseError = (event) => this.setError(event.reason);

  setError = (error) => {
    this.setState({
      hasError: true,
      error: error instanceof Error ? error : new Error(error),
    });
  };

  handleContactSupport = () => this.setState({ showModal: true });

  handleCloseModal = () => this.setState({ showModal: false });

  render() {
    const { children } = this.props;
    const { hasError, error, showModal } = this.state;

    if (!hasError) return children;

    const errorDetails = error?.stack || error?.message || "Unknown error";
    const pageUrl = window.location.href;

    return (
      <>
        <Box
          sx={{
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            px: 3,
          }}
        >
          <Stack spacing={2.5} alignItems="center" maxWidth={700} width="100%">
            <SvgToIcon IconLogo={CloudError} width="170px" height="170px" />
            <Typography variant="h6" align="center">
              Task failed successfully
            </Typography>
            <Typography variant="body1" align="center">
              We did something! Just not the thing you wanted. <br />
              Contact support and we'll figure out what the thing actually was.
            </Typography>
            <Stack direction="row" spacing={1.5}>
              <ContainedButton
                color="primary"
                size="medium"
                onClick={navigateHome}
              >
                Back to home
              </ContainedButton>
              <OutlinedButton
                onClick={this.handleContactSupport}
                color="inherit"
                size="medium"
              >
                Contact support
              </OutlinedButton>
            </Stack>
          </Stack>
        </Box>
        {showModal && (
          <ContactSupportModal
            open={showModal}
            onClose={this.handleCloseModal}
            errorDetails={errorDetails}
            pageUrl={pageUrl}
          />
        )}
      </>
    );
  }
}

function GlobalErrorBoundary({ children }) {
  return <GlobalErrorBoundaryInner>{children}</GlobalErrorBoundaryInner>;
}

export default GlobalErrorBoundary;
